import { useTheme } from "@mui/material/styles";
import React, { useEffect, useMemo, useState } from "react";
import ViewEditDialog from "../../../utils/ui-components/ViewEditDialog";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Grow,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MainCard from "../../../utils/ui-components/MainCard";
import SearchIcon from "@mui/icons-material/Search";
import RequestMask from "./request-mask";
import { MaskService } from "../../../assets/_services/mask-service";
import PropTypes from "prop-types";
import { useLocation, useParams } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import TextFieldWrapper from "../../../utils/ui-components/FormsUI/TextField";

import TheaterComedyIcon from "@mui/icons-material/TheaterComedy";
import { DataGrid, GridAlignment } from "@mui/x-data-grid";
import EmptyResultDataGrid from "../../../utils/ui-components/EmptyResultDataGrid";
import AutoCompleteSearchWrapper from "../../../utils/ui-components/FormsUI/AutoCompleteSearch";
import { UserService } from "../../../assets/_services/user-service";
import StopIcon from "@mui/icons-material/Stop";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { closeConfirmDialog, openConfirmDialog } from "../../../utils/ui-components/pop-ups/ConfirmDialog";
import { openSuccessDialog } from "../../../utils/ui-components/pop-ups/SuccessDialog";
import { useDispatch } from "react-redux";
import { MENU_OPEN, SET_BREADCRUMBS } from "../../../redux/actions/actions";
import MultipleSelectCheckmarksWrapper from "../../../utils/ui-components/FormsUI/MultipleSelectCheckmarks";

export interface MaskListValues {
  maskId: number | string;
  maskName: string;
  purpose: string;
  activeStatus: boolean;
  parentUser: { value: number | string; label: string };
  operators: string[] | number[];
}

MasksList.propTypes = {
  role: PropTypes.number.isRequired,
};

function MasksList({ role }: any) {
  const { id } = useParams();
  const theme: any = useTheme();
  const dispatch = useDispatch();
  const matchDownSM = useMediaQuery(theme.breakpoints.down("md"));

  const INITIAL_FORM_STATE: MaskListValues = {
    maskId: "",
    maskName: "",
    purpose: "",
    activeStatus: true,
    parentUser: { value: "", label: "" },
    operators: [],
  };

  const [search, setSearch] = useState<{
    maskName: string;
    parentUser: string | number;
    parentUserId: { value: number | string; label: string };
    operators: string[] | number[];
  }>({
    maskName: "",
    parentUser: "",
    parentUserId: { value: "All", label: "All" },
    operators: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [masks, setMasks] = useState([]);
  const [isLoadingStatus, setIsLoadingStatus] = useState<number | string>("");

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [count, setCount] = useState(0);

  const [open, setOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [initialItem, setInitialItem] = useState(INITIAL_FORM_STATE);
  const path = useLocation();

  useEffect(() => {
    const id = path?.pathname;
    dispatch({ type: MENU_OPEN, id });
    dispatch({
      type: SET_BREADCRUMBS,
      payload: [
        { name: "Home", path: "/Dashboard", bold: true, state: null },
        { name: "Masks", path: null, bold: false, state: null },
      ],
    });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchMasksList(search);
  }, [paginationModel, search]);

  const fetchMasksList = (searchValue: {
    maskName: string;
    parentUser: string | number;
    parentUserId: { value: number | string; label: string };
    operators: string[] | number[];
  }) => {
    setIsLoading(true);
    let formData = {
      ownerId: searchValue.parentUserId.value === "All" || searchValue.parentUserId.value === "" ? "" : searchValue.parentUserId.value,
      maskName: searchValue.maskName,
      systemOperatorId: searchValue.operators.map((item: any) => item.value),
    };
    // if (formData?.ownerId) {
    //   MaskService.getMaskListByOwnerId(formData, paginationModel.page, paginationModel.pageSize).then((response) => {
    //     if (response.isSuccess) {
    //       setMasks(
    //         response.data.data.rows.map((e: any) => ({
    //           maskId: e.id,
    //           maskName: e.maskName,
    //           purpose: e.purpose,
    //           userName: searchValue.parentUserId.label,
    //           companyName: searchValue.parentUser,
    //           status: e.status,
    //         }))
    //       );
    //       setCount(response.data.data.count);
    //     }
    //     setIsLoading(false);
    //   });
    // } else {
    MaskService.getAllMaskList(formData, paginationModel.page, paginationModel.pageSize).then((response) => {
      if (response.isSuccess) {
        setMasks(response.data.data.rows);
        setCount(response.data.data.count);
      }
      setIsLoading(false);
    });
    // }
  };

  const handleClickOpen = (dialogTitle: string, formState: MaskListValues) => {
    setOpen(true);
    setDialogTitle(dialogTitle);
    setInitialItem(formState);
  };

  const dialog = useMemo(
    () =>
      ViewEditDialog(RequestMask)({
        open: open,
        setOpen: setOpen,
        dialogTitle: dialogTitle,
        initialItem: initialItem,
        fetchData: () => {
          fetchMasksList(search);
        },
        theme: theme,
        maxWidth: "sm",
        initialData: { role: role, creator: id },
      }),
    [open]
  );

  const changeStatus = (data: { maskId: number; status: number }) => {
    setIsLoadingStatus(data.maskId);
    closeConfirmDialog();
    let formData = {
      maskId: data.maskId,
      status: data.status === 1 ? 0 : 1,
    };
    MaskService.changeMaskStatus(formData).then((response) => {
      if (response.isSuccess) {
        openSuccessDialog("Success", "Mask status changed successfully");
        fetchMasksList(search);
        setIsLoadingStatus("");
      } else {
        setIsLoadingStatus("");
      }
    });
  };

  const columns = [
    {
      field: "action",
      filterable: false,
      headerName: "",
      sortable: false,
      disableExport: false,
      width: 60,
      headerClassName: "stickyActionHeader",
      cellClassName: "stickyColumn",
      renderCell: (params: any) => {
        return (
          <Tooltip title="Change Status">
            <IconButton
              aria-label="view"
              color="primary"
              onClick={() => {
                openConfirmDialog(
                  params.row.status ? "Deactive Mask" : "Active Mask",
                  `Are you sure you want to ${params.row.status ? "deactive" : "active"} this mask?`,
                  {
                    maskId: params.row?.maskId,
                    status: params.row?.status,
                  },
                  (data) => changeStatus(data)
                );
              }}
            >
              {params.row.id === isLoadingStatus ? (
                <CircularProgress size={"20px"} />
              ) : params.row.status ? (
                <StopIcon fontSize="medium" color="error" />
              ) : (
                <PlayArrowIcon fontSize="medium" color="success" />
              )}
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: "maskId",
      filterable: true,
      headerName: "ID",
      sortable: true,
      disableExport: false,
      maxWidth: 100,
      minWidth: 80,
      flex: 1,
    },
    {
      field: "maskName",
      filterable: true,
      headerName: "Mask Name",
      sortable: true,
      disableExport: false,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "userName",
      filterable: true,
      headerName: "Parent User",
      sortable: true,
      disableExport: false,
      minWidth: 200,
      flex: 1,
      renderCell: (params: any) => {
        return (
          <Stack direction={"column"}>
            <Typography>{params?.row?.companyName}</Typography>
            <Typography variant="caption">{params?.row?.userName}</Typography>
          </Stack>
        );
      },
    },
    {
      field: "operator",
      filterable: true,
      headerName: "Operator",
      sortable: true,
      disableExport: false,
      minWidth: 100,
      flex: 1,
      renderCell: (params: any) => {
        return params?.row?.operatorName === "robi"
          ? "Robi"
          : params?.row?.operatorName === "tl"
          ? "Teletalk"
          : params?.row?.operatorName === "bl"
          ? "Banglalink"
          : params?.row?.operatorName === "gp"
          ? "Grameenphone"
          : "";
      },
    },
    {
      field: "status",
      filterable: true,
      headerName: "Status",
      sortable: true,
      disableExport: false,
      maxWidth: 130,
      minWidth: 100,
      align: "center" as GridAlignment,
      flex: 1,
      renderCell: (params: any) => {
        return params?.row?.status ? (
          <Chip variant="outlined" color="success" sx={{ borderRadius: 2 }} label="Active" />
        ) : (
          <Chip variant="outlined" color="error" label="Inactive" sx={{ borderRadius: 2 }} />
        );
      },
    },
    {
      field: "purpose",
      filterable: true,
      headerName: "purpose",
      sortable: true,
      disableExport: false,
      minWidth: 200,
      flex: 1,
    },
  ];

  return (
    <Grid container justifyContent="center" spacing={2}>
      <Grid item lg={11} md={12} xs={12}>
        <Typography sx={{ ...theme.typography.pageheader }}>
          <TheaterComedyIcon fontSize="medium" /> Mask Management
        </Typography>
      </Grid>
      <Grid item lg={11} md={12} sm={12} xs={12}>
        <Formik
          initialValues={{
            maskName: "",
            parentUser: { value: "All", label: "All" },
            operators: [],
          }}
          validationSchema={Yup.object().shape({
            maskName: Yup.string().notRequired(),
            parentUser: Yup.object().notRequired().typeError("Select a parent user from the list"),
            operators: Yup.array().of(
              Yup.object().shape({
                value: Yup.number().notRequired(),
              })
            ),
          })}
          onSubmit={(values: any, { setSubmitting }) => {
            setPaginationModel({ ...paginationModel, page: 0 });
            setSearch({
              maskName: values.maskName,
              parentUser: values.parentUser,
              parentUserId: values.parentUser,
              operators: values.operators,
            });
            // fetchMasksList(values.maskName);
          }}
        >
          {({ values, dirty, isSubmitting, isValid, setFieldValue }) => (
            <Form>
              <Grid container spacing={1} justifyContent={"flex-end"}>
                <Grid item xl={2} lg={2} md={3} xs={4}>
                  <AutoCompleteSearchWrapper
                    // sx={{ mt: 1 }}
                    disabled={false}
                    freeSolo
                    name={"parentUser"}
                    label={"Parent User"}
                    data={values}
                    fetchData={async (value: any, data: any) => {
                      try {
                        let searchData: {
                          companyName: string;
                        };

                        if (typeof data.user === "object" && value.value === data.user?.label && typeof data.user?.value === "number") {
                        } else if (value.value === "" || value.value === "All") {
                          return { isSuccess: true, data: [] };
                        } else {
                          searchData = {
                            companyName: String(value?.value),
                          };
                          const response = await UserService.searchOwnerByCompanyName(searchData);
                          if (response.isSuccess) {
                            const parents = [
                              { label: "All", value: "All" },
                              ...response.data?.data?.map((e: any) => ({
                                label: `${e.companyName} (${e.userName})`,
                                value: e.ownerId,
                              })),
                            ];
                            return { isSuccess: true, data: parents };
                          } else {
                            return { isSuccess: false, data: [{ label: "All", value: "All" }] };
                          }
                        }
                      } catch (error) {
                        return { isSuccess: false, data: [{ label: "All", value: "All" }] };
                      }
                    }}
                  />
                </Grid>
                <Grid item xl={2} lg={2} md={3} xs={4}>
                  <MultipleSelectCheckmarksWrapper
                    name="operators"
                    label="Operators"
                    options={[
                      { value: 1, label: "Robi" },
                      { value: 2, label: "Teletalk" },
                      { value: 3, label: "Banglalink" },
                      { value: 4, label: "Grameenphone" },
                    ]}
                  />
                </Grid>
                <Grid item>
                  <TextFieldWrapper
                    label="Mask name"
                    placeholder="Search mask.."
                    name={"maskName"}
                    sx={{ minWidth: "250px" }}
                    size={"small"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item>
                  <Button color="primary" variant="contained" type="submit" sx={{ ...theme.typography.customInput }}>
                    View
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={open}
                    type="button"
                    fullWidth
                    sx={{ ...theme.typography.customInput }}
                    onClick={() => handleClickOpen(`Add Mask`, INITIAL_FORM_STATE)}
                    startIcon={<AddIcon />}
                  >
                    Create Mask
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Grid>

      <Grid item lg={open ? 7 : 11} md={open ? 7 : 12} sm={12} xs={12}>
        <Box sx={{ minHeight: 400, width: "100%" }}>
          <DataGrid
            autoHeight
            columns={columns}
            loading={isLoading}
            rows={masks}
            slots={{
              noRowsOverlay: EmptyResultDataGrid,
              loadingOverlay: () => <LinearProgress />,
            }}
            style={{ overflow: "auto" }}
            sx={{
              "& .MuiDataGrid-cell.stickyColumn": {
                position: "sticky",
                left: 0,
                backgroundColor: `${theme.palette.mode === "light" ? theme.palette.primary.light : theme.palette.background.paper}`,
                zIndex: 1,
              },
              "& .clicked-row": {
                backgroundColor: `${theme.palette.mode === "light" ? theme.palette.secondary.light : theme.palette.background.footer} !important`,
              },
              cursor: "pointer",
            }}
            getRowClassName={(params) => (params.row.maskId === initialItem.maskId ? "clicked-row" : "")}
            pagination
            getRowId={(row: any) => row.maskId}
            paginationMode="server"
            rowCount={count}
            disableRowSelectionOnClick
            pageSizeOptions={[5, 10, 25, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
          />
        </Box>
      </Grid>
      {open && !matchDownSM ? (
        <Grow in={open && !matchDownSM} timeout={500}>
          <Grid item lg={5} md={5}>
            <MainCard title="Create Mask">
              <RequestMask
                initialItem={initialItem}
                setOpen={setOpen}
                fetchData={() => {
                  fetchMasksList(search);
                }}
                initialData={{ role: role, creator: id }}
              />
            </MainCard>
          </Grid>
        </Grow>
      ) : (
        dialog
      )}
    </Grid>
  );
}

export default MasksList;
